import type { Identity } from "../types";

interface HeaderProps {
  identity: Identity;
  headline: string;
  onJumpToYear?: (yearMonth: string) => void;
}

export function Header({ identity, headline, onJumpToYear }: HeaderProps) {
  const tags = headline
    .split("|")
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <header
      className="relative px-10 pt-7 pb-6 border-b"
      style={{ borderColor: "var(--color-rule)" }}
    >
      <div className="flex items-end gap-10">
        {/* Dateline kicker */}
        <div className="flex flex-col gap-1.5 min-w-0">
          <div className="kicker flex items-baseline gap-2">
            {identity.location && (
              <>
                <span style={{ color: "var(--color-copper)" }}>
                  {identity.location.toUpperCase()}
                </span>
                <span style={{ color: "var(--color-cream-dim)" }}>—</span>
              </>
            )}
            <span>An Almanac</span>
          </div>
          <h1
            className="display text-[44px] leading-[0.95] tracking-tight"
            style={{ color: "var(--color-cream)", fontWeight: 400 }}
          >
            {identity.name || "Career Timeline"}
            <span
              className="display-italic ml-1"
              style={{
                color: "var(--color-copper)",
                fontWeight: 300,
                fontSize: "0.55em",
                verticalAlign: "0.5em",
              }}
            >
              ·
            </span>
          </h1>
        </div>

        {/* Tag rail */}
        <div className="flex-1 min-w-0 hidden md:flex flex-wrap gap-x-4 gap-y-1 pb-1.5 items-baseline">
          {tags.slice(0, 5).map((tag, i) => (
            <span
              key={i}
              className="display-italic text-[13px] leading-none"
              style={{
                color: i === 0 ? "var(--color-cream-soft)" : "var(--color-cream-dim)",
                fontWeight: 400,
              }}
            >
              {tag}
              {i < tags.length - 1 && i < 4 && (
                <span className="ml-4" style={{ color: "var(--color-rule)" }}>
                  /
                </span>
              )}
            </span>
          ))}
        </div>

        {/* Nav: typographic toggles, no boxes */}
        <nav className="flex items-baseline gap-5 pb-1.5">
          <NavButton onClick={() => onJumpToYear?.("1997-03")}>1997</NavButton>
          <NavButton onClick={() => onJumpToYear?.("2024-01")} primary>
            2024
          </NavButton>
          <NavButton onClick={() => onJumpToYear?.("now")}>Now</NavButton>
        </nav>
      </div>
    </header>
  );
}

function NavButton({
  onClick,
  children,
  primary = false,
}: {
  onClick: () => void;
  children: React.ReactNode;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mono group relative text-[11px] tracking-[0.18em] uppercase transition-colors duration-200"
      style={{
        color: primary ? "var(--color-copper)" : "var(--color-cream-soft)",
        fontWeight: primary ? 500 : 400,
      }}
    >
      <span>{children}</span>
      <span
        className="absolute -bottom-1 left-0 right-0 h-px transition-transform duration-300 origin-left scale-x-0 group-hover:scale-x-100"
        style={{
          background: primary ? "var(--color-copper)" : "var(--color-cream)",
        }}
      />
    </button>
  );
}
